/**
 * Supabase Client Utility
 * 
 * This file provides a centralized Supabase client instance for use throughout the application.
 * It ensures we're using environment variables correctly and only initializing once.
 */

import { createClient } from '@supabase/supabase-js';
import { reportErrorToAdmin } from './reportError';

// REACT_APP_* vars are mapped from canonical names (SUPABASE_URL, SUPABASE_ANON_KEY)
// by craco.config.js at build time, so only one entry is needed in .env.
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  const configurationError = new Error('Missing Supabase environment variables. Cannot initialize supabaseClient.');
  reportErrorToAdmin({
    source: 'utils/supabaseClient',
    error: configurationError,
    context: {
      hasUrl: Boolean(supabaseUrl),
      hasAnonKey: Boolean(supabaseAnonKey),
    }
  }).catch((reportError) => {
    console.error('[supabaseClient] Failed to report configuration error', reportError);
  });
  throw configurationError;
}

export const supabase = createClient(
  supabaseUrl,
  supabaseAnonKey
);

/**
 * Example usage:
 * 
 * import { supabase } from '../utils/supabaseClient';
 * 
 * // Authentication
 * const signIn = async (email, password) => {
 *   const { data, error } = await supabase.auth.signInWithPassword({
 *     email,
 *     password,
 *   });
 *   return { data, error };
 * };
 * 
 * // Database operations
 * const fetchUserProfile = async (userId) => {
 *   const { data, error } = await supabase
 *     .from('profiles')
 *     .select('*')
 *     .eq('id', userId)
 *     .single();
 *   return { data, error };
 * };
 */
